import { useEffect, useMemo, useState } from "react";
import { get, post } from "../api";
import type {
  BillingPlanRow,
  Charge,
  DialerCharge,
  MetricCampaign,
  Plan,
  PlanOverride,
  PlanPreview,
  PlatformDomainRow,
  Pulse,
  Region,
  Tier,
} from "../api";
import { money, monthLabel, ruleName, threshold } from "./format";

/**
 * Edits a billing plan and saves it as the next version.
 *
 * The draft is previewed against real months as it is typed, through the
 * same validation the save runs -- a preview that accepted what the save
 * refuses would be worse than none. A save names the version it was
 * edited from, so two admins editing one plan cannot silently overwrite
 * each other: the second is told to reload.
 *
 * `customer` absent edits the default plan -- the flat estimate for
 * tenants mapped to nobody -- which takes no overrides and has no preview.
 */
export default function PlanEditor({
  customer,
  onSaved,
}: {
  customer?: { id: string; slug: string; name: string };
  onSaved?: (row: BillingPlanRow) => void;
}) {
  const [versions, setVersions] = useState<BillingPlanRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const listPath = customer
    ? `/api/customers/${customer.slug}/billing-plans`
    : "/api/admin/billing-default-plan";

  useEffect(() => {
    setVersions(null);
    void get<BillingPlanRow[]>(listPath)
      .then(setVersions)
      .catch((e) => setError(e.message));
  }, [listPath]);

  if (error) return <div className="error">{error}</div>;
  if (!versions) return <div className="empty">Loading…</div>;
  return <Form key={versions[0]?.id ?? "new"} customer={customer} latest={versions[0]} onSaved={onSaved} />;
}

const blankPlan = (): Plan => ({
  bot: {
    pulse: { rules: [], default_pulse_secs: 60 },
    tiers: [{ label: "Tier 1", from_minutes: 0, rate_per_min: "0.00" }],
  },
  dialer: null,
  overrides: [],
});

const thisMonth = () => new Date().toISOString().slice(0, 7);

/** The rate_per_call keys a pulse charges per call, in rule order. */
export function callKeys(p: Pulse): string[] {
  const keys = p.rules.filter((r) => r.charge === "per_call").map((r) => String(r.up_to_secs));
  if (p.default_charge === "per_call") keys.push("default");
  return keys;
}

/**
 * Keep a tier grid's per-call prices in step with an edited pulse: a rule
 * newly charged per call gets a blank price in every tier, a rule no
 * longer per call loses its prices, and a rule whose length was edited
 * keeps its prices under its new key. The server refuses a missing or a
 * stray price; this is what keeps the editor from ever sending one.
 */
export function syncCallRates(before: Pulse, after: Pulse, tiers: Tier[]): Tier[] {
  const rename = new Map<string, string>();
  // Same number of rules: an edit in place, so a rule keeps its prices
  // across a change of length. Otherwise rules were added or removed, and
  // the ones that remain still carry their old lengths.
  if (before.rules.length === after.rules.length) {
    before.rules.forEach((r, i) => rename.set(String(r.up_to_secs), String(after.rules[i].up_to_secs)));
  }
  const keys = callKeys(after);
  return tiers.map((t) => {
    const old = t.rate_per_call ?? {};
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(old)) {
      const nk = rename.get(k) ?? k;
      if (keys.includes(nk)) next[nk] = v;
    }
    for (const k of keys) if (!(k in next)) next[k] = "";
    const { rate_per_call: _drop, ...rest } = t;
    return Object.keys(next).length ? { ...rest, rate_per_call: next } : rest;
  });
}

function Form({
  customer,
  latest,
  onSaved,
}: {
  customer?: { id: string; slug: string; name: string };
  latest?: BillingPlanRow;
  onSaved?: (row: BillingPlanRow) => void;
}) {
  const [plan, setPlan] = useState<Plan>(() => structuredClone(latest?.plan ?? blankPlan()));
  const [currency, setCurrency] = useState(latest?.currency ?? "INR");
  const [effective, setEffective] = useState(latest?.effective_from.slice(0, 7) ?? thisMonth());
  const [note, setNote] = useState(latest?.note ?? "");
  const [unit, setUnit] = useState<"h" | "min">("h");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const path = customer
        ? `/api/admin/customers/${customer.id}/billing-plans`
        : "/api/admin/billing-default-plan";
      const row = await post<BillingPlanRow>(path, {
        plan,
        currency,
        effective_from: effective,
        note,
        base_version: latest?.version ?? 0,
      });
      setSaved(`Saved as version ${row.version}.`);
      onSaved?.(row);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rev-editor rev-grid">
      <div>
        <div className="row">
          <label>
            Takes effect from{" "}
            <input type="month" value={effective} onChange={(e) => setEffective(e.target.value)} />
          </label>
          <label>
            Currency{" "}
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              <option>INR</option>
              <option>MYR</option>
            </select>
          </label>
          <label>
            Thresholds in{" "}
            <select value={unit} onChange={(e) => setUnit(e.target.value as "h" | "min")}>
              <option value="h">hours / month</option>
              <option value="min">minutes / month</option>
            </select>
          </label>
        </div>
        <div className="row">
          <input
            placeholder="Source and anything the plan cannot express (e.g. Annexure 2, signed 18-May-2026)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="wide"
          />
        </div>
        <p className="dim">
          A new version applies from its month onward; months before it keep the version they were
          priced under. Frozen months change only when they are recomputed.
        </p>

        <fieldset>
          <legend>AI voice bot</legend>
          <PulseEditor
            value={plan.bot.pulse}
            onChange={(p) =>
              setPlan({
                ...plan,
                bot: { pulse: p, tiers: syncCallRates(plan.bot.pulse, p, plan.bot.tiers) },
                // An override with its own rates but the customer's pulse
                // prices the customer's per-call rules too.
                overrides: plan.overrides.map((o) =>
                  o.bot?.tiers && !o.bot.pulse
                    ? { ...o, bot: { ...o.bot, tiers: syncCallRates(plan.bot.pulse, p, o.bot.tiers) } }
                    : o,
                ),
              })
            }
          />
          <TierEditor
            value={plan.bot.tiers}
            keys={callKeys(plan.bot.pulse)}
            unit={unit}
            currency={currency}
            onChange={(t) => setPlan({ ...plan, bot: { ...plan.bot, tiers: t } })}
          />
          <p className="dim">
            The whole month is billed at the tier its usage reaches; below the first tier it is billed
            at the first tier's rate and flagged as below commitment.
          </p>
        </fieldset>

        <fieldset>
          <legend>Dialer</legend>
          <label>
            <input
              type="checkbox"
              checked={plan.dialer !== null}
              onChange={(e) =>
                setPlan({
                  ...plan,
                  dialer: e.target.checked
                    ? {
                        pulse: { rules: [], default_pulse_secs: 60 },
                        tiers: [{ label: "Flat", from_minutes: 0, rate_per_min: "0.00" }],
                        tier_basis: "bot_minutes",
                      }
                    : null,
                })
              }
            />{" "}
            Arrowhead's dialer is billed
          </label>
          {plan.dialer && (
            <DialerEditor
              value={plan.dialer}
              unit={unit}
              currency={currency}
              onChange={(d) => setPlan({ ...plan, dialer: d })}
            />
          )}
        </fieldset>

        {customer && (
          <Overrides
            slug={customer.slug}
            plan={plan}
            unit={unit}
            currency={currency}
            onChange={(o) => setPlan({ ...plan, overrides: o })}
          />
        )}

        {error && <div className="error">{error}</div>}
        {saved && <p className="dim">{saved}</p>}
        <button disabled={saving} onClick={save}>
          {saving ? "Saving…" : `Save as version ${(latest?.version ?? 0) + 1}`}
        </button>
      </div>

      <div>
        <PulseTester plan={plan} currency={currency} />
        {customer && <Preview customerId={customer.id} plan={plan} currency={currency} />}
      </div>
    </div>
  );
}

function ChargeSelect({ value, onChange }: { value?: Charge; onChange: (c: Charge) => void }) {
  return (
    <select value={value ?? "per_minute"} onChange={(e) => onChange(e.target.value as Charge)} aria-label="Charged">
      <option value="per_minute">charged per minute</option>
      <option value="per_call">charged per call</option>
    </select>
  );
}

function PulseEditor({ value, onChange }: { value: Pulse; onChange: (p: Pulse) => void }) {
  const setRule = (i: number, patch: Partial<Pulse["rules"][number]>) =>
    onChange({ ...value, rules: value.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <div>
      <div className="dim">
        Pulse — every call counts toward usage in its pulses; the charge decides whether it is paid
        per minute or as one call
      </div>
      {value.rules.map((r, i) => (
        <div className="row" key={i}>
          Calls up to
          <input type="number" min={1} value={r.up_to_secs} onChange={(e) => setRule(i, { up_to_secs: Number(e.target.value) })} />
          s count in
          <input type="number" min={1} value={r.pulse_secs} onChange={(e) => setRule(i, { pulse_secs: Number(e.target.value) })} />
          s pulses,
          <ChargeSelect value={r.charge} onChange={(c) => setRule(i, { charge: c })} />
          <button className="btn tiny" onClick={() => onChange({ ...value, rules: value.rules.filter((_, j) => j !== i) })}>
            Remove
          </button>
        </div>
      ))}
      <div className="row">
        {value.rules.length ? "All other calls" : "Every call"} count in
        <input
          type="number"
          min={1}
          value={value.default_pulse_secs}
          onChange={(e) => onChange({ ...value, default_pulse_secs: Number(e.target.value) })}
        />
        s pulses{value.default_pulse_secs === 1 && <span className="dim">(per second)</span>},
        <ChargeSelect value={value.default_charge} onChange={(c) => onChange({ ...value, default_charge: c })} />
        <button
          className="btn tiny"
          onClick={() =>
            onChange({
              ...value,
              rules: [...value.rules, { up_to_secs: (value.rules.at(-1)?.up_to_secs ?? 0) + 10, pulse_secs: 10 }],
            })
          }
        >
          + Short-call rule
        </button>
      </div>
    </div>
  );
}

function TierEditor({
  value,
  keys,
  unit,
  currency,
  onChange,
}: {
  value: Tier[];
  /** The pulse's per-call rules: one ₹/call column each. */
  keys: string[];
  unit: "h" | "min";
  currency: string;
  onChange: (t: Tier[]) => void;
}) {
  const factor = unit === "h" ? 60 : 1;
  const sym = currency === "INR" ? "₹" : "RM";
  const set = (i: number, patch: Partial<Tier>) => onChange(value.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const setCall = (i: number, key: string, v: string) =>
    set(i, { rate_per_call: { ...(value[i].rate_per_call ?? {}), [key]: v } });
  return (
    <div>
      <div className="dim">Tiers</div>
      {value.map((t, i) => (
        <div className="row" key={i}>
          <input className="label" value={t.label} onChange={(e) => set(i, { label: e.target.value })} />
          from
          <input
            type="number"
            min={0}
            value={t.from_minutes / factor}
            onChange={(e) => set(i, { from_minutes: Math.round(Number(e.target.value) * factor) })}
          />
          {unit === "h" ? "h/month" : "min/month"}
          <span>{currency === "INR" ? "₹" : "RM"}</span>
          <input className="rate" inputMode="decimal" value={t.rate_per_min} onChange={(e) => set(i, { rate_per_min: e.target.value })} />
          /min
          {keys.map((k) => (
            <span key={k} className="rev-callrate">
              {ruleName(k)} {sym}
              <input
                className={`rate${(t.rate_per_call?.[k] ?? "") === "" ? " missing" : ""}`}
                inputMode="decimal"
                placeholder="0.00"
                value={t.rate_per_call?.[k] ?? ""}
                onChange={(e) => setCall(i, k, e.target.value)}
              />
              /call
            </span>
          ))}
          {value.length > 1 && (
            <button className="btn tiny" onClick={() => onChange(value.filter((_, j) => j !== i))}>
              Remove
            </button>
          )}
        </div>
      ))}
      <button
        className="btn tiny"
        onClick={() => {
          const last = value.at(-1);
          onChange([
            ...value,
            {
              label: `Tier ${value.length + 1}`,
              from_minutes: (last?.from_minutes ?? 0) + 60 * 500,
              rate_per_min: last?.rate_per_min ?? "0.00",
              ...(keys.length ? { rate_per_call: { ...(last?.rate_per_call ?? {}) } } : {}),
            },
          ]);
        }}
      >
        + Tier
      </button>
    </div>
  );
}

function DialerEditor({
  value,
  unit,
  currency,
  onChange,
}: {
  value: DialerCharge;
  unit: "h" | "min";
  currency: string;
  onChange: (d: DialerCharge) => void;
}) {
  return (
    <>
      <PulseEditor
        value={value.pulse}
        onChange={(p) => onChange({ ...value, pulse: p, tiers: syncCallRates(value.pulse, p, value.tiers) })}
      />
      <TierEditor
        value={value.tiers}
        keys={callKeys(value.pulse)}
        unit={unit}
        currency={currency}
        onChange={(t) => onChange({ ...value, tiers: t })}
      />
      <div className="row">
        Dialer tier chosen on{" "}
        <select value={value.tier_basis} onChange={(e) => onChange({ ...value, tier_basis: e.target.value as DialerCharge["tier_basis"] })}>
          <option value="bot_minutes">the customer's bot usage</option>
          <option value="dialer_minutes">dialer minutes</option>
        </select>
      </div>
    </>
  );
}

// --- Overrides ------------------------------------------------------------

/** An override whose pulse charges a rule per call while its rates are the
    customer's, and the customer's rates carry no price for that rule. The
    server refuses the save with the same reason; this says so first. */
function InheritedRatesWarning({ pulse, tiers, inherited }: { pulse: Pulse; tiers?: Tier[]; inherited: Tier[] }) {
  if (tiers) return null;
  const missing = callKeys(pulse).filter((k) => inherited.some((t) => !(k in (t.rate_per_call ?? {}))));
  if (missing.length === 0) return null;
  return (
    <p className="rev-warn">
      This override charges {missing.map(ruleName).join(", ")} per call, but the customer's rates have
      no per-call price for it. Tick Own rates to price it.
    </p>
  );
}

type Target = { kind: "domain" | "campaign"; region: Region; id: string; label: string; group: string };

function matchKey(o: PlanOverride): string {
  return "domain" in o.match
    ? `domain:${o.match.domain.region}:${o.match.domain.domain_id}`
    : `campaign:${o.match.campaign.region}:${o.match.campaign.campaign_id}`;
}

function Overrides({
  slug,
  plan,
  unit,
  currency,
  onChange,
}: {
  slug: string;
  plan: Plan;
  unit: "h" | "min";
  currency: string;
  onChange: (o: PlanOverride[]) => void;
}) {
  const [targets, setTargets] = useState<Target[]>([]);
  const [pick, setPick] = useState("");

  useEffect(() => {
    void Promise.all([
      get<{ domains: PlatformDomainRow[] }>(`/api/customers/${slug}/platform-domains`).catch(() => ({ domains: [] })),
      get<{ campaigns: MetricCampaign[] }>(`/api/customers/${slug}/metrics/campaigns`).catch(() => ({ campaigns: [] })),
    ]).then(([d, c]) => {
      const out: Target[] = [];
      for (const dom of d.domains) {
        out.push({
          kind: "domain",
          region: dom.region,
          id: dom.domain_id,
          label: dom.identifier ?? dom.domain_id,
          group: "Domains",
        });
      }
      for (const cam of c.campaigns) {
        out.push({
          kind: "campaign",
          region: cam.region as Region,
          id: cam.campaign_id,
          label: cam.campaign_name ?? cam.campaign_identifier ?? cam.campaign_id,
          group: `Campaigns · ${cam.domain_identifier ?? cam.domain_id}`,
        });
      }
      setTargets(out);
    });
  }, [slug]);

  const used = new Set(plan.overrides.map(matchKey));
  const free = targets.filter((t) => !used.has(`${t.kind}:${t.region}:${t.id}`));
  const groups = [...new Set(free.map((t) => t.group))];
  const nameOf = (o: PlanOverride) => {
    const key = matchKey(o);
    const t = targets.find((t) => `${t.kind}:${t.region}:${t.id}` === key);
    return t ? `${t.kind === "domain" ? "Domain" : "Campaign"} · ${t.label} (${t.region})` : `${key} — no longer this customer's`;
  };

  function add() {
    const t = free.find((t) => `${t.kind}:${t.region}:${t.id}` === pick);
    if (!t) return;
    const match: PlanOverride["match"] =
      t.kind === "domain"
        ? { domain: { region: t.region, domain_id: t.id } }
        : { campaign: { region: t.region, campaign_id: t.id } };
    onChange([
      ...plan.overrides,
      { match, label: t.label, bot: { tiers: structuredClone(plan.bot.tiers) }, separate_pool: false },
    ]);
    setPick("");
  }

  const set = (i: number, o: PlanOverride) => onChange(plan.overrides.map((x, j) => (j === i ? o : x)));

  return (
    <fieldset>
      <legend>Overrides</legend>
      <p className="dim">
        A campaign override beats a domain override, which beats the customer plan. Whatever an
        override leaves unticked is inherited from the customer plan. Tiers are chosen on the
        customer's total usage unless an override keeps its own pool.
      </p>
      {plan.overrides.map((o, i) => (
        <div className="override" key={matchKey(o)}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <strong>{nameOf(o)}</strong>
            <button className="btn tiny" onClick={() => onChange(plan.overrides.filter((_, j) => j !== i))}>
              Remove
            </button>
          </div>
          <div className="row">
            Label <input value={o.label} onChange={(e) => set(i, { ...o, label: e.target.value })} />
          </div>
          <label>
            <input
              type="checkbox"
              checked={!!o.bot?.pulse}
              onChange={(e) => {
                const pulse = e.target.checked ? structuredClone(plan.bot.pulse) : undefined;
                const eff = pulse ?? plan.bot.pulse;
                set(i, {
                  ...o,
                  bot: { ...o.bot, pulse, tiers: o.bot?.tiers && syncCallRates(eff, eff, o.bot.tiers) },
                });
              }}
            />{" "}
            Own pulse
          </label>
          {o.bot?.pulse && (
            <PulseEditor
              value={o.bot.pulse}
              onChange={(p) =>
                set(i, {
                  ...o,
                  bot: { ...o.bot, pulse: p, tiers: o.bot?.tiers && syncCallRates(o.bot.pulse!, p, o.bot.tiers) },
                })
              }
            />
          )}
          <label>
            <input
              type="checkbox"
              checked={!!o.bot?.tiers}
              onChange={(e) => {
                const eff = o.bot?.pulse ?? plan.bot.pulse;
                set(i, {
                  ...o,
                  bot: {
                    ...o.bot,
                    tiers: e.target.checked ? syncCallRates(eff, eff, structuredClone(plan.bot.tiers)) : undefined,
                  },
                });
              }}
            />{" "}
            Own rates
          </label>
          {o.bot?.tiers && (
            <TierEditor
              value={o.bot.tiers}
              keys={callKeys(o.bot.pulse ?? plan.bot.pulse)}
              unit={unit}
              currency={currency}
              onChange={(t) => set(i, { ...o, bot: { ...o.bot, tiers: t } })}
            />
          )}
          <InheritedRatesWarning
            pulse={o.bot?.pulse ?? plan.bot.pulse}
            tiers={o.bot?.tiers}
            inherited={plan.bot.tiers}
          />
          <div className="row">
            Dialer{" "}
            <select
              value={o.dialer === undefined ? "inherit" : o.dialer.enabled ? "own" : "off"}
              onChange={(e) => {
                const v = e.target.value;
                set(i, {
                  ...o,
                  dialer:
                    v === "inherit"
                      ? undefined
                      : v === "off"
                        ? { enabled: false }
                        : {
                            enabled: true,
                            pulse: { rules: [], default_pulse_secs: 60 },
                            tiers: [{ label: "Flat", from_minutes: 0, rate_per_min: "0.00" }],
                            tier_basis: "bot_minutes",
                          },
                });
              }}
            >
              <option value="inherit">as the customer plan</option>
              <option value="off">not billed</option>
              <option value="own">its own rates</option>
            </select>
          </div>
          {o.dialer?.enabled && o.dialer.pulse && o.dialer.tiers && (
            <DialerEditor
              value={{ pulse: o.dialer.pulse, tiers: o.dialer.tiers, tier_basis: o.dialer.tier_basis ?? "bot_minutes" }}
              unit={unit}
              currency={currency}
              onChange={(d) => set(i, { ...o, dialer: { enabled: true, ...d } })}
            />
          )}
          <label>
            <input type="checkbox" checked={o.separate_pool} onChange={(e) => set(i, { ...o, separate_pool: e.target.checked })} />{" "}
            Choose its tier on its own usage (a separate commitment)
          </label>
        </div>
      ))}
      <div className="row">
        <select value={pick} onChange={(e) => setPick(e.target.value)} style={{ minWidth: 280 }}>
          <option value="">Add an override for…</option>
          {groups.map((g) => (
            <optgroup key={g} label={g}>
              {free
                .filter((t) => t.group === g)
                .map((t) => (
                  <option key={`${t.kind}:${t.region}:${t.id}`} value={`${t.kind}:${t.region}:${t.id}`}>
                    {t.label} ({t.region})
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
        <button className="btn tiny" disabled={!pick} onClick={add}>
          + Override
        </button>
      </div>
    </fieldset>
  );
}

// --- Side panel -----------------------------------------------------------

/** A length in, billable seconds out, under the draft's base pulses. */
/** One length in; what it counts toward usage and what it costs, at the
    first tier, for the bot and the dialer. Mirrors `Pulse::bill`. */
function PulseTester({ plan, currency }: { plan: Plan; currency: string }) {
  const [secs, setSecs] = useState(25);
  const sym = currency === "INR" ? "₹" : "RM ";
  const describe = (p: Pulse, tiers: Tier[], what: string) => {
    if (secs <= 0) return `${what}: nothing`;
    const rule = p.rules.find((r) => secs <= r.up_to_secs);
    const pulse = Math.max(1, rule?.pulse_secs ?? p.default_pulse_secs);
    const charge = rule ? rule.charge : p.default_charge;
    const key = rule ? String(rule.up_to_secs) : "default";
    const usage = Math.ceil(secs / pulse) * pulse;
    const tier = tiers[0];
    if (charge === "per_call") {
      const price = tier?.rate_per_call?.[key];
      return `${what}: counts ${usage}s toward usage, charged ${price ? `${sym}${price}` : "its per-call price"} per call at ${tier?.label ?? "the first tier"}`;
    }
    const cost = tier ? (usage / 60) * Number(tier.rate_per_min) : NaN;
    return `${what}: ${usage}s charged per minute${Number.isFinite(cost) ? ` (${sym}${cost.toFixed(2)} at ${tier.label})` : ""}`;
  };
  return (
    <div className="rev-plan">
      <strong>Pulse tester</strong>
      <div className="row">
        A call of{" "}
        <input type="number" min={1} value={secs} onChange={(e) => setSecs(Number(e.target.value))} style={{ width: 80 }} />s
      </div>
      <div className="dim">{describe(plan.bot.pulse, plan.bot.tiers, "Bot")}</div>
      {plan.dialer && <div className="dim">{describe(plan.dialer.pulse, plan.dialer.tiers, "Dialer")}</div>}
    </div>
  );
}

function Preview({ customerId, plan, currency }: { customerId: string; plan: Plan; currency: string }) {
  const [rows, setRows] = useState<PlanPreview[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const body = useMemo(() => JSON.stringify(plan), [plan]);

  useEffect(() => {
    const t = setTimeout(() => {
      setError(null);
      void post<PlanPreview[]>(`/api/admin/customers/${customerId}/billing-plans/preview`, { plan })
        .then(setRows)
        .catch((e) => setError(e.message));
    }, 400);
    return () => clearTimeout(t);
    // `body` stands for the plan's content; the object identity changes on
    // every keystroke whether or not anything did.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, body]);

  return (
    <div className="rev-plan">
      <strong>Preview</strong>
      <p className="dim">This draft against real usage, beside the plan in effect.</p>
      {error && <div className="error">{error}</div>}
      {rows && (
        <div className="ctable" style={{ margin: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th className="n">Draft</th>
                <th className="n">Now</th>
                <th className="n">Change</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const diff = Number(r.draft.total_amount) - Number(r.current.total_amount);
                return (
                  <tr key={r.month}>
                    <td>
                      {monthLabel(r.month)} {r.to_date && <span className="dim">to date</span>}
                    </td>
                    <td className="n">
                      {money(r.draft.total_amount, currency)}
                      <div className="dim">
                        {r.draft.tier_label}
                        {r.draft.below_mmc_minutes && " · below commitment"}
                      </div>
                    </td>
                    <td className="n">
                      {money(r.current.total_amount, currency)}
                      <div className="dim">
                        {r.current.tier_label}
                        {r.current_estimated && " · estimated"}
                      </div>
                    </td>
                    <td className="n">
                      {diff === 0 ? "—" : `${diff > 0 ? "+" : "−"}${money(Math.abs(diff), currency)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="dim">
        Tier thresholds: {plan.bot.tiers.map((t) => `${t.label} ${threshold(t.from_minutes)}`).join(" · ")}
      </p>
    </div>
  );
}
